import { FC, MouseEventHandler, CSSProperties } from 'react';
import { Breakpoint, useMediaQuery, useTheme, Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import WhiteLogo from './assets/logo-o2/o2-logo-nomargin.svg';
import BlueLogo from './assets/logo-o2/o2-logo-nomargin-blue.svg';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';

interface O2AppLogoProps {
  /**
   * Name of O2 application
   */
  name?: string;

  /**
   * Breakpoint for hide name. Under this breakpoint will be only O2 visible.
   */
  hideName?: Breakpoint | number;

  /**
   * Click handler
   */
  onClick?: MouseEventHandler;

  /**
   * color of logo
   */
  color?: 'blue' | 'white';
}

interface AppNameProps {
  /**
   * text color
   */
  color?: 'blue' | 'white';
}

const AppName = styled(Box, {
  name: 'AppName',
  slot: 'root',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'textColor',
})<AppNameProps>(({ theme, color }) => ({
  color: color === 'white' ? theme.palette.common.white : theme.palette.primary.main,
  fontFamily: theme.typography.fontFamily,
  fontWeight: 600,
  fontSize: '1.5rem',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
  },
}));

const Image = styled('img', {
  name: 'O2AppLogo',
  slot: 'logo',
})(({ theme }) => ({
  height: '56px',
  maxWidth: '100%',
  maxHeight: '100%',
  [theme.breakpoints.down('sm')]: {
    height: '48px',
  },
}));

/**
 * O2 application logo with custom name. Some applications have their own specific logo, such as MojeO2.
 * In some cases, the application name may be too long for smaller screens, in which case a breakpoint can be set to hide the application name.
 */
const O2AppLogo: FC<O2AppLogoProps> = ({ name, onClick, hideName, color }) => {
  const theme = useTheme();
  const hide = useMediaQuery(theme.breakpoints['down'](hideName ?? 'sm'));

  return (
    <Stack
      direction="row"
      spacing={{ xs: 1, sm: 2 }}
      alignItems="center"
      onClick={onClick}
      sx={{ cursor: onClick ? 'pointer' : undefined }}
    >
      <Image src={color === 'white' ? WhiteLogo : BlueLogo} alt={`O2 ${name}`} />
      {((hideName && !hide) || !hideName) && name && <AppName color={color}>{name}</AppName>}
    </Stack>
  );
};

export default O2AppLogo;
